import { EChartsType } from "echarts/core";

const SERIES_ROLE_DICTIONARY: Record<string, { group: string; role: string; kind: string }> = {
  "QH1-leading": { group: "baseCurve", role: "base q-h leading", kind: "line" },
  QH1: { group: "baseCurve", role: "base q-h", kind: "line" },
  "QEFF-leading": { group: "baseCurve", role: "base q-eff leading", kind: "line" },
  QEFF: { group: "baseCurve", role: "base q-eff", kind: "line" },
  "QP1-leading": { group: "baseCurve", role: "base q-p leading", kind: "line" },
  QP1: { group: "baseCurve", role: "base q-p", kind: "line" },
  QNPSH: { group: "baseCurve", role: "base q-npshr", kind: "line" },
  aux: { group: "resultCurve", role: "auxiliary line", kind: "line" },
  "QH2-leading": { group: "resultCurve", role: "result q-h leading", kind: "line" },
  QH2: { group: "resultCurve", role: "result q-h", kind: "line" },
  "QEFF2-leading": { group: "resultCurve", role: "result q-eff leading", kind: "line" },
  QEFF2: { group: "resultCurve", role: "result q-eff", kind: "line" },
  "QP2-leading": { group: "resultCurve", role: "result q-p leading", kind: "line" },
  QP2: { group: "resultCurve", role: "result q-p", kind: "line" },
  QNPSH2: { group: "resultCurve", role: "result q-npshr", kind: "line" },
  "intersection point": { group: "marker", role: "intersection marker", kind: "custom" },
  "top info": { group: "infoPanel", role: "top info panel", kind: "custom" },
  "bottom info": { group: "infoPanel", role: "bottom info panel", kind: "custom" },
};

const getSeriesColor = (series: any) =>
  series?.lineStyle?.color || series?.itemStyle?.color || series?.color;

export const buildOptionSeriesSummary = (seriesList: any[] = []) =>
  seriesList.map((series: any, index: number) => {
    const name = series?.name || `series-${index}`;
    const role = SERIES_ROLE_DICTIONARY[name] || {
      group: "unknown",
      role: "unknown",
      kind: series?.type || "unknown",
    };
    const data = Array.isArray(series?.data) ? series.data : [];

    return {
      index,
      id: series?.id,
      name,
      type: series?.type,
      group: role.group,
      role: role.role,
      kind: role.kind,
      xAxisIndex: series?.xAxisIndex ?? 0,
      yAxisIndex: series?.yAxisIndex ?? 0,
      smooth: series?.smooth,
      silent: series?.silent,
      color: getSeriesColor(series),
      lineWidth: series?.lineStyle?.width,
      lineDash: series?.lineStyle?.type,
      dataCount: data.length,
    };
  });

const summarizeChartView = (view: any, index: number) => {
  const model = view?.__model;
  const groupChildren =
    typeof view?.group?.childrenRef === "function"
      ? view.group.childrenRef().length
      : Array.isArray(view?.group?._children)
        ? view.group._children.length
        : undefined;

  return {
    index,
    uid: view?.uid,
    type: view?.type,
    renderTaskType: view?.renderTask?._reset?.name || typeof view?.renderTask?._reset,
    seriesName: model?.name,
    seriesId: model?.id,
    seriesType: model?.type,
    seriesSubType: model?.subType,
    groupChildCount: groupChildren,
  };
};

const summarizeDisplayObject = (item: any, index: number) => {
  const shape = item?.shape || {};
  const points = Array.isArray(shape?.points) ? shape.points : [];
  const firstPoint = points.length > 0 ? points[0] : [shape?.x1, shape?.y1];
  const lastPoint = points.length > 0 ? points[points.length - 1] : [shape?.x2, shape?.y2];
  const text = item?.style?.text;

  return {
    index,
    type: item?.type,
    z: item?.z,
    z2: item?.z2,
    invisible: item?.invisible,
    ignore: item?.ignore,
    silent: item?.silent,
    stroke: item?.style?.stroke,
    fill: item?.style?.fill,
    opacity: item?.style?.opacity,
    lineWidth: item?.style?.lineWidth,
    lineDash: item?.style?.lineDash,
    text: typeof text === "string" ? text.slice(0, 120) : undefined,
    firstPoint,
    lastPoint,
    pointCount: points.length,
    anid: item?.anid,
    name: item?.name,
  };
};

const isSlantedLineSegment = (item: any) => {
  if (item?.type !== "line") {
    return false;
  }

  const firstPoint = item?.firstPoint || [];
  const lastPoint = item?.lastPoint || [];

  if (firstPoint.length < 2 || lastPoint.length < 2) {
    return false;
  }

  const dx = Math.abs((lastPoint[0] || 0) - (firstPoint[0] || 0));
  const dy = Math.abs((lastPoint[1] || 0) - (firstPoint[1] || 0));

  return dx > 2 && dy > 2;
};

export const buildPerformanceChartDebugPayload = (
  instance: EChartsType,
  extra: Record<string, any> = {}
) => {
  const dom: any = instance.getDom?.();
  const optionSnapshot: any = instance.getOption();
  const optionSeries = buildOptionSeriesSummary(optionSnapshot?.series || []);
  const xAxisSummary = (optionSnapshot?.xAxis || []).map((axis: any, index: number) => ({
    index,
    name: axis?.name,
    type: axis?.type,
    splitNumber: axis?.splitNumber,
    splitLineColor: axis?.splitLine?.lineStyle?.color,
    splitLineWidth: axis?.splitLine?.lineStyle?.width,
    minorSplitLineColor: axis?.minorSplitLine?.lineStyle?.color,
    minorSplitLineWidth: axis?.minorSplitLine?.lineStyle?.width,
    axisLineColor: axis?.axisLine?.lineStyle?.color,
    axisTickColor: axis?.axisTick?.lineStyle?.color,
  }));
  const yAxisSummary = (optionSnapshot?.yAxis || []).map((axis: any, index: number) => ({
    index,
    name: axis?.name,
    type: axis?.type,
    splitNumber: axis?.splitNumber,
    splitLineColor: axis?.splitLine?.lineStyle?.color,
    splitLineWidth: axis?.splitLine?.lineStyle?.width,
    minorSplitLineColor: axis?.minorSplitLine?.lineStyle?.color,
    minorSplitLineWidth: axis?.minorSplitLine?.lineStyle?.width,
    axisLineColor: axis?.axisLine?.lineStyle?.color,
    axisTickColor: axis?.axisTick?.lineStyle?.color,
  }));
  const expectedSeriesNames = new Set(optionSeries.map((series) => series.name));
  const chartsViews = (((instance as any)?._chartsViews) || []).map(summarizeChartView);
  const chartsMapKeys = Object.keys(((instance as any)?._chartsMap) || {});
  const zrStorage: any = (instance as any)?.getZr?.()?.storage;
  const displayList: any[] = zrStorage?.getDisplayList?.() || [];
  const domCanvasSummary = dom
    ? Array.from(dom.querySelectorAll("canvas")).map((canvas: any, index: number) => ({
        index,
        width: canvas.width,
        height: canvas.height,
        styleWidth: canvas.style?.width,
        styleHeight: canvas.style?.height,
        stylePosition: canvas.style?.position,
        styleLeft: canvas.style?.left,
        styleTop: canvas.style?.top,
        styleZIndex: canvas.style?.zIndex,
        stylePointerEvents: canvas.style?.pointerEvents,
        className: canvas.className,
      }))
    : [];
  const lineDisplayList = displayList
    .filter((item: any) => item?.type === "polyline" || item?.type === "line" || item?.type === "bezier-curve")
    .map(summarizeDisplayObject);
  const curveDisplayObjects = lineDisplayList.filter(
    (item: any) => item?.type === "polyline" || item?.type === "bezier-curve" || item?.pointCount > 2
  );
  const nonCurveStrokeSet = new Set([
    "#d6dde5",
    "#eceff3",
    "#39434d",
    "#5f6a76",
    "#6f8192",
  ]);
  const curveLikeDisplayObjects = curveDisplayObjects.filter((item: any) => {
    const stroke = item?.stroke;
    return Boolean(stroke) && !nonCurveStrokeSet.has(stroke);
  });
  const unmappedDisplayObjects = lineDisplayList.filter((item: any) => {
    const nameCandidates = [item.name, item.anid].filter(Boolean);
    return !nameCandidates.some((candidate: string) => expectedSeriesNames.has(candidate));
  });
  const unmappedCurveDisplayObjects = curveDisplayObjects.filter((item: any) => {
    const nameCandidates = [item.name, item.anid].filter(Boolean);
    return !nameCandidates.some((candidate: string) => expectedSeriesNames.has(candidate));
  });
  const slantedLineSegments = lineDisplayList.filter(isSlantedLineSegment);
  const slantedLineSegmentsByStroke = Object.values(
    slantedLineSegments.reduce((acc: Record<string, any>, item: any) => {
      const stroke = item?.stroke || "__none__";

      if (!acc[stroke]) {
        acc[stroke] = {
          stroke,
          count: 0,
          samples: [],
        };
      }

      acc[stroke].count += 1;

      if (acc[stroke].samples.length < 8) {
        acc[stroke].samples.push({
          index: item.index,
          firstPoint: item.firstPoint,
          lastPoint: item.lastPoint,
          lineWidth: item.lineWidth,
          lineDash: item.lineDash,
          anid: item.anid,
          name: item.name,
        });
      }

      return acc;
    }, {})
  );

  return {
    ...extra,
    optionSeries,
    xAxisSummary,
    yAxisSummary,
    domCanvasSummary,
    explicitSeriesCount: optionSeries.length,
    chartsMapKeys,
    chartViews: chartsViews,
    displayList: lineDisplayList,
    displayLineObjectCount: lineDisplayList.length,
    curveDisplayObjects,
    curveLikeDisplayObjects,
    unmappedDisplayObjects,
    unmappedCurveDisplayObjects,
    slantedLineSegmentsByStroke,
  };
};
